// Looking at a picture someone sent, without leaving the conversation.
//
// Clicking an image used to open the raw attachment in a new browser tab: you lost the
// thread, and getting back meant closing a tab. A picture in a chat should open where
// you are and close again just as easily — Escape, the X, or clicking the backdrop.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download } from 'lucide-react';

export interface ImageLightboxProps {
  src: string;
  alt: string;
  /** Shown under the image; the file name is the useful caption. */
  caption?: string;
  /** Direct link for saving the original. */
  downloadHref?: string;
  /** Save handler (wins over downloadHref) — e.g. save a fetched blob under its original name. */
  onDownload?: () => void;
  downloadLabel?: string;
  closeLabel?: string;
  onClose: () => void;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  src, alt, caption, downloadHref, onDownload, downloadLabel = 'Download', closeLabel = 'Close', onClose,
}) => {
  // Escape closes, and the page behind must not scroll while the viewer is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[1100] flex flex-col items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
    >
      {/* The controls sit on the backdrop, so their clicks must not ALSO reach it —
          otherwise closing fires twice and Download would close the viewer. */}
      <div className="absolute right-3 top-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {onDownload ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            aria-label={downloadLabel}
            title={downloadLabel}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <Download className="h-5 w-5" />
          </button>
        ) : downloadHref && (
          <a
            href={downloadHref}
            target="_blank"
            rel="noreferrer"
            aria-label={downloadLabel}
            title={downloadLabel}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <Download className="h-5 w-5" />
          </a>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          autoFocus
          aria-label={closeLabel}
          title={closeLabel}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Clicks on the picture itself must not close it — only the backdrop does. */}
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl"
      />
      {caption && (
        <p onClick={(e) => e.stopPropagation()} className="mt-3 max-w-full truncate text-sm text-white/80">
          {caption}
        </p>
      )}
    </div>,
    document.body,
  );
};

export default ImageLightbox;
